import { CSSProperties } from 'react'
import { pxToRem } from 'src/utils/getFontValue'

import { typography as typeTokens } from './tokens.generated'

// ----------------------------------------------------------------------
// Console type scale (tier 3), assembled from the generated design tokens.
//
// The system names eleven console roles; MUI ships fourteen variants. The map
// below is the whole translation, and it is deliberately many-to-one where MUI
// carried duplicates: `subtitle1` is a group title, `h6` and `subtitle2` are
// both a subhead. Two 15px semibolds that differed only in line height were
// drift, not hierarchy.
//
// 24px is the ceiling for the whole console. `display` is Stack Sans Headline
// and is reserved for sign-in and the few brand moments — it is not reachable
// from a console page.
// ----------------------------------------------------------------------

const FONT_PRIMARY = typeTokens.families.ui.stack
const FONT_DISPLAY = typeTokens.families.display.stack

const CONSOLE = typeTokens.console

// `display` variant — Stack Sans Headline, used sparingly for hero numbers/text.
declare module '@mui/material/styles' {
  interface TypographyVariants {
    display: CSSProperties
  }
  interface TypographyVariantsOptions {
    display?: CSSProperties
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display: true
  }
}

type ConsoleRole = keyof typeof CONSOLE

/** Turn a token role into the CSS a MUI variant needs. */
const variant = (role: ConsoleRole) => {
  const token = CONSOLE[role] as {
    size: number
    lineHeight: number
    weight: number
    letterSpacing?: string
    caps?: boolean
  }
  return {
    fontWeight: token.weight,
    lineHeight: token.lineHeight,
    fontSize: pxToRem(token.size),
    ...(token.letterSpacing ? { letterSpacing: token.letterSpacing } : {}),
    ...(token.caps ? { textTransform: 'uppercase' as const } : {}),
  }
}

const typography = {
  fontFamily: FONT_PRIMARY,
  fontWeightRegular: typeTokens.weights.regular,
  fontWeightMedium: typeTokens.weights.semibold,
  fontWeightBold: typeTokens.weights.bold,
  display: { fontFamily: FONT_DISPLAY, ...variant('display') },
  h1: variant('h1'),
  h2: variant('h2'),
  h3: variant('h3'),
  h4: variant('cardTitle'),
  h5: variant('groupTitle'),
  h6: variant('subhead'),
  subtitle1: variant('groupTitle'),
  subtitle2: variant('subhead'),
  body1: variant('body'),
  body2: variant('small'),
  caption: variant('caption'),
  // The system's 11px caps field label. `overline` is the MUI slot that carries
  // it; there is deliberately no second name for the same thing.
  overline: variant('label'),
  button: { ...variant('button'), textTransform: 'none' as const },
} as const

/**
 * Tabular numerals. Mandatory for anything counted, metered or counted down,
 * and for money, plates, VINs and distances — proportional digits make a live
 * column jitter on every tick and a money column lose its right edge.
 */
export const tabularNumerals = {
  fontVariantNumeric: typeTokens.numeric.fontVariant,
} as const

export default typography
